import React, { useEffect, useMemo, useState } from 'react'
import { DcButton, DcDropDown, DcInput, PixelFactoryContext } from 'pixel-base'
import styled from 'styled-components'
import {
  addJournalEntry,
  getCostCenters,
  getJournals,
  getPartners,
  getPostingAccounts,
} from 'utils/api-routes-accounts'
import SaveIcon from 'utils/icons/save-icon'
import TrashIcon from 'utils/icons/trash-icon'
import FinanceCard from '../../../components/foundation/FinanceCard'
import FinanceInlineAlert from '../../../components/foundation/FinanceInlineAlert'
import { financeColors, financeRadius } from '../../../components/tokens'

interface AccountOption {
  id: number
  code: string
  name: string
  default_partner_required?: boolean
  default_cost_center_required?: boolean
}

interface PartyOption {
  id: number
  code: string
  name: string
}

interface JournalEntryLine {
  account_id: number
  description: string
  partner_id: number | null
  cost_center_id: number | null
  due_date: string
  reference: string
  debit_base: number
  credit_base: number
}

interface AddNewEntryProps {
  handleClose: (_response: any, _action: string) => void
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const IntroTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${financeColors.textStrong};
`

const IntroText = styled.div`
  font-size: 13px;
  color: ${financeColors.textMuted};
`

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const HeaderStack = styled.div`
  display: grid;
  gap: 12px;
`

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

const LineStack = styled.div`
  display: grid;
  gap: 12px;
`

const LineCard = styled.div`
  border: 1px solid ${financeColors.border};
  border-radius: ${financeRadius.lg};
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  padding: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
`

const LineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`

const LineTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${financeColors.textStrong};
  font-size: 13px;
  font-weight: 700;
`

const LineBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${financeColors.surfaceInfo};
  color: ${financeColors.infoText};
  font-size: 11px;
  font-weight: 700;
`

const PrimaryLineGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1.2fr 1.2fr 0.9fr 0.9fr;
  gap: 10px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }
`

const SecondaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr minmax(180px, 1fr);
  gap: 10px;
  margin-top: 10px;
  align-items: center;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const LineMeta = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-radius: ${financeRadius.md};
  background: ${financeColors.surfaceMuted};
  color: ${financeColors.textMuted};
  font-size: 12px;
  border: 1px solid ${financeColors.border};
`

const TotalsBar = styled.div<{ $balanced: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`

const TotalCard = styled.div<{ $highlight?: boolean; $balanced?: boolean }>`
  border: 1px solid
    ${(props) => {
      if (!props.$highlight) return financeColors.border
      return props.$balanced
        ? financeColors.successBorder
        : financeColors.dangerBorder
    }};
  background: ${(props) => {
    if (!props.$highlight) return financeColors.surface
    return props.$balanced ? financeColors.successBg : financeColors.dangerBg
  }};
  border-radius: ${financeRadius.md};
  padding: 12px 14px;
`

const TotalLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${financeColors.textMuted};
  margin-bottom: 6px;
`

const TotalValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${financeColors.textStrong};
`

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: sticky;
  bottom: 0;
  padding-top: 4px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.76) 0%,
    #ffffff 24%
  );
`

const HelperText = styled.div`
  font-size: 12px;
  color: ${financeColors.textMuted};
`

const emptyLine = (): JournalEntryLine => ({
  account_id: 0,
  description: '',
  partner_id: null,
  cost_center_id: null,
  due_date: '',
  reference: '',
  debit_base: 0,
  credit_base: 0,
})

const AddNewEntry: React.FC<AddNewEntryProps> = ({ handleClose }) => {
  const { showSuccess, showFailure } = React.useContext(
    PixelFactoryContext,
  ) as any

  const [journals, setJournals] = useState<Array<{ id: number; name: string }>>(
    [],
  )
  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [partners, setPartners] = useState<PartyOption[]>([])
  const [costCenters, setCostCenters] = useState<PartyOption[]>([])
  const [data, setData] = useState({
    journal_id: 0,
    document_date: new Date().toISOString().split('T')[0],
    posting_date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    idempotency_key: '',
    lines: [emptyLine()],
  })

  useEffect(() => {
    const journalsSubscription = getJournals().subscribe({
      next: (res) => {
        const items = res.response?.data || []
        setJournals(items)
        if (items.length > 0) {
          setData((prev) => ({ ...prev, journal_id: items[0].id }))
        }
      },
      error: () => showFailure('Failed to fetch journals'),
    })

    const accountsSubscription = getPostingAccounts().subscribe({
      next: (res) => setAccounts(res.response?.data || []),
      error: () => showFailure('Failed to fetch posting accounts'),
    })

    const partnersSubscription = getPartners().subscribe({
      next: (res) => setPartners(res.response?.data || []),
      error: () => showFailure('Failed to fetch partners'),
    })

    const costCentersSubscription = getCostCenters().subscribe({
      next: (res) => setCostCenters(res.response?.data || []),
      error: () => showFailure('Failed to fetch cost centers'),
    })

    return () => {
      journalsSubscription.unsubscribe()
      accountsSubscription.unsubscribe()
      partnersSubscription.unsubscribe()
      costCentersSubscription.unsubscribe()
    }
  }, [showFailure])

  const totalDebit = useMemo(
    () =>
      data.lines.reduce((sum, line) => sum + Number(line.debit_base || 0), 0),
    [data.lines],
  )
  const totalCredit = useMemo(
    () =>
      data.lines.reduce((sum, line) => sum + Number(line.credit_base || 0), 0),
    [data.lines],
  )

  const isBalanced = totalDebit === totalCredit && totalDebit > 0

  const getAccountMeta = (accountId: number) =>
    accounts.find((account) => Number(account.id) === Number(accountId))

  const addNewLine = () => {
    setData((prev) => ({
      ...prev,
      lines: [...prev.lines, emptyLine()],
    }))
  }

  const removeLine = (index: number) => {
    setData((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }))
  }

  const updateLine = (
    index: number,
    field: keyof JournalEntryLine,
    value: string | number | null,
  ) => {
    setData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    }))
  }

  const validateEntry = () => {
    if (!data.journal_id || !data.reference || data.lines.length < 2) {
      showFailure('Journal, reference, and at least two lines are required')
      return false
    }

    if (totalDebit === 0 && totalCredit === 0) {
      showFailure('At least one debit or credit amount is required')
      return false
    }

    if (totalDebit !== totalCredit) {
      showFailure('Journal entry must stay balanced before saving')
      return false
    }

    for (const line of data.lines) {
      if (!line.account_id) {
        showFailure('Each line must have an account')
        return false
      }
      const account = getAccountMeta(line.account_id)
      if (account?.default_partner_required && !line.partner_id) {
        showFailure(
          `Partner is required for account ${account.code} - ${account.name}`,
        )
        return false
      }
      if (account?.default_cost_center_required && !line.cost_center_id) {
        showFailure(
          `Cost center is required for account ${account.code} - ${account.name}`,
        )
        return false
      }
      if (
        Number(line.debit_base || 0) > 0 &&
        Number(line.credit_base || 0) > 0
      ) {
        showFailure('A line cannot contain both debit and credit amounts')
        return false
      }
      if (
        Number(line.debit_base || 0) === 0 &&
        Number(line.credit_base || 0) === 0
      ) {
        showFailure('Each line must include either a debit or credit amount')
        return false
      }
    }

    return true
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateEntry()) {
      return
    }

    addJournalEntry({
      ...data,
      idempotency_key: data.idempotency_key || `manual-entry-${Date.now()}`,
    }).subscribe({
      next: () => {
        showSuccess('Journal entry saved as draft')
        handleClose({}, 'add')
      },
      error: () => showFailure('Failed to save journal entry'),
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Intro>
        <IntroText>
          Record a balanced manual entry with posting-ready lines and
          dimensions.
        </IntroText>
      </Intro>

      <FinanceCard
        title="Entry Header"
        description="Set the journal, dates, and reference before entering your lines."
      >
        <HeaderGrid>
          <HeaderStack>
            <DcDropDown
              value={data.journal_id}
              options={journals.map((journal) => ({
                value: journal.id,
                label: journal.name,
              }))}
              onChange={(e) =>
                setData({ ...data, journal_id: Number(e.target.value) })
              }
            />
            <DcInput
              value={data.reference}
              placeholder="Reference"
              onChange={(e) => setData({ ...data, reference: e.target.value })}
            />
          </HeaderStack>
          <HeaderStack>
            <DcInput
              value={data.description}
              placeholder="Description"
              onChange={(e) =>
                setData({ ...data, description: e.target.value })
              }
            />
            <DcInput
              value={data.idempotency_key}
              placeholder="Idempotency Key (optional)"
              onChange={(e) =>
                setData({ ...data, idempotency_key: e.target.value })
              }
            />
          </HeaderStack>
        </HeaderGrid>
        <DateGrid style={{ marginTop: 12 }}>
          <DcInput
            type="date"
            value={data.document_date}
            onChange={(e) =>
              setData({ ...data, document_date: e.target.value })
            }
          />
          <DcInput
            type="date"
            value={data.posting_date}
            onChange={(e) => setData({ ...data, posting_date: e.target.value })}
          />
        </DateGrid>
      </FinanceCard>

      <FinanceCard
        title="Lines"
        description="Each row must contain one posting account and either a debit or a credit."
        actions={
          <DcButton
            type="button"
            variant="outline-secondary"
            onClick={addNewLine}
          >
            Add Line
          </DcButton>
        }
      >
        <LineStack>
          {data.lines.map((line, index) => {
            const account = getAccountMeta(line.account_id)
            const notes = [] as string[]
            if (account?.default_partner_required)
              notes.push('Partner required')
            if (account?.default_cost_center_required)
              notes.push('Cost center required')

            return (
              <LineCard key={index}>
                <LineHeader>
                  <LineTitle>
                    <LineBadge>{index + 1}</LineBadge>
                    Entry Line
                  </LineTitle>
                  <DcButton
                    type="button"
                    variant="outline-danger"
                    onClick={() => removeLine(index)}
                  >
                    <TrashIcon size={16} />
                  </DcButton>
                </LineHeader>

                <PrimaryLineGrid>
                  <DcDropDown
                    value={line.account_id}
                    options={(accounts || []).map((entryAccount) => ({
                      value: entryAccount.id,
                      label: `${entryAccount.code} - ${entryAccount.name}`,
                    }))}
                    onChange={(e) =>
                      updateLine(index, 'account_id', Number(e.target.value))
                    }
                  />
                  <DcInput
                    value={line.description}
                    placeholder="Line description"
                    onChange={(e) =>
                      updateLine(index, 'description', e.target.value)
                    }
                  />
                  <DcDropDown
                    value={line.partner_id || ''}
                    options={[
                      { value: '', label: 'Partner (optional)' },
                      ...partners.map((partner) => ({
                        value: partner.id,
                        label: `${partner.code} - ${partner.name}`,
                      })),
                    ]}
                    onChange={(e) =>
                      updateLine(
                        index,
                        'partner_id',
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                  <DcInput
                    type="number"
                    value={line.debit_base}
                    placeholder="Debit"
                    onChange={(e) =>
                      updateLine(index, 'debit_base', Number(e.target.value))
                    }
                  />
                  <DcInput
                    type="number"
                    value={line.credit_base}
                    placeholder="Credit"
                    onChange={(e) =>
                      updateLine(index, 'credit_base', Number(e.target.value))
                    }
                  />
                </PrimaryLineGrid>

                <SecondaryRow>
                  <DcDropDown
                    value={line.cost_center_id || ''}
                    options={[
                      { value: '', label: 'Cost Center (optional)' },
                      ...costCenters.map((costCenter) => ({
                        value: costCenter.id,
                        label: `${costCenter.code} - ${costCenter.name}`,
                      })),
                    ]}
                    onChange={(e) =>
                      updateLine(
                        index,
                        'cost_center_id',
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                  <DcInput
                    type="date"
                    value={line.due_date}
                    placeholder="Due date"
                    onChange={(e) =>
                      updateLine(index, 'due_date', e.target.value)
                    }
                  />
                  <DcInput
                    value={line.reference}
                    placeholder="Line reference"
                    onChange={(e) =>
                      updateLine(index, 'reference', e.target.value)
                    }
                  />
                </SecondaryRow>

                <SecondaryRow style={{ marginTop: 8 }}>
                  <LineMeta>
                    {account
                      ? `${account.code} • ${account.name}`
                      : 'Select an account'}
                  </LineMeta>
                  <LineMeta>{notes.join(' • ') || 'Posting account'}</LineMeta>
                  <LineMeta>
                    {Number(line.debit_base || 0) > 0 &&
                    Number(line.credit_base || 0) > 0
                      ? 'Invalid: debit and credit both entered'
                      : Number(line.debit_base || 0) === 0 &&
                        Number(line.credit_base || 0) === 0
                      ? 'Awaiting amount'
                      : Number(line.debit_base || 0) > 0
                      ? 'Debit line'
                      : 'Credit line'}
                  </LineMeta>
                </SecondaryRow>
              </LineCard>
            )
          })}
        </LineStack>
      </FinanceCard>

      {!isBalanced && (totalDebit > 0 || totalCredit > 0) ? (
        <FinanceInlineAlert
          tone="warning"
          message="This entry is currently out of balance. Total debit must equal total credit before you can save it."
        />
      ) : null}

      <TotalsBar $balanced={isBalanced}>
        <TotalCard>
          <TotalLabel>Total Debit</TotalLabel>
          <TotalValue>{totalDebit.toFixed(2)}</TotalValue>
        </TotalCard>
        <TotalCard>
          <TotalLabel>Total Credit</TotalLabel>
          <TotalValue>{totalCredit.toFixed(2)}</TotalValue>
        </TotalCard>
        <TotalCard $highlight $balanced={isBalanced}>
          <TotalLabel>Status</TotalLabel>
          <TotalValue>{isBalanced ? 'Balanced' : 'Out of Balance'}</TotalValue>
        </TotalCard>
      </TotalsBar>

      <FooterBar>
        <HelperText>
          Save as draft first. Posted entries become immutable and should be
          corrected with reversal.
        </HelperText>
        <DcButton variant="primary" type="submit">
          <SaveIcon size={16} /> <span>Save Draft</span>
        </DcButton>
      </FooterBar>
    </Form>
  )
}

export default AddNewEntry
